import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { EndPointApi } from '@helpers/endpointapi';
import { Shipyard } from 'icreate/common/models/shipyard';
import { getSimplifiedData, ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { BehaviorSubject, lastValueFrom, retry, Subject, takeUntil } from 'rxjs';
import { ShipyardService } from './shipyard.service';
import { AccessRequestSocketService } from './access-request-socket.service';
import { AccessRequest, AccessRequestStatus } from 'icreate/common/models/access-request';
import { AuthService } from './auth.service';
import { RuntimeEnvironments } from '../models/runtime-enviroment.enum';
import { EnvironmentService } from './environment.service';

@Injectable({
    providedIn: 'root',
})
export class VesselService implements OnDestroy {
    private domain: string;
    private headers: any = new HttpHeaders({
        'Content-Type': 'application/json',
    });

    private activeVessel: Vessel;
    private _activeVessel$ = new BehaviorSubject<Vessel>(null);
    activeVessel$ = this._activeVessel$.asObservable();

    private _canEditVessel: boolean = false;
    get canEditVessel() {
        return this._canEditVessel;
    }

    private destroySubscriptionSubject$ = new Subject<void>();
    private _accessUpdated$ = new BehaviorSubject<boolean>(false);
    accessUpdated$ = this._accessUpdated$.asObservable();

    private env: RuntimeEnvironments;
    constructor(
        private http: HttpClient,
        private shipyardService: ShipyardService,
        private accessRequestSocketService: AccessRequestSocketService,
        private authService: AuthService,
        private environmentService: EnvironmentService
    ) {
        this.domain = EndPointApi.iApiDomain();
        this.env = this.environmentService.getEnvironment();

        this.accessRequestSocketService
            .startedEditing$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((accessRequest: AccessRequest) => {
                this.checkEditAccess(accessRequest);
            });

        this.accessRequestSocketService
            .finishedEditing$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((accessRequest: AccessRequest) => {
                this.checkEditAccess(accessRequest);
            });
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    async setActiveVessel(vessel: Vessel) {
        this.activeVessel = vessel;
        this._activeVessel$.next(vessel);
        if (vessel) {
            const shipyardList = await this.shipyardService.getList();
            const selectedShipyard = shipyardList?.find(s => s.id === vessel.shipyardId);
            this.shipyardService.setSelectedShipyard(selectedShipyard?.name);
        } else {
            this.shipyardService.setSelectedShipyard(null);
        }

        this.updateCanEditVessel();
    }

    getActiveVessel() {
        return this.activeVessel;
    }

    getActiveVesselId() {
        return this.activeVessel?.id;
    }

    async updateVesselInList(updatedVessel: Vessel) {
        const shipyardList: Shipyard[] = await this.shipyardService.getList();
        const shipyard = shipyardList.find(s => s.id === updatedVessel.shipyardId);

        if (shipyard) {
            shipyard.vessels.forEach((vessel, index) => {
                if (vessel.id === updatedVessel.id) {
                    shipyard.vessels[index] = updatedVessel;
                }
            });
            this.shipyardService.updateShipyardList(shipyardList);
        }
    }

    async getVessel(vesselId): Promise<Vessel> {
        return lastValueFrom(this.http.get<Vessel>(`${this.domain}/api/vessel/${vesselId}`));
    }

    async create(vesselData) {
        vesselData.skipGuard = true;
        return lastValueFrom(
            this.http.post(`${this.domain}/api/vessel`, vesselData, {
                headers: this.headers,
            })
        );
    }

    async duplicate(vessel: Vessel, previousVesselId) {
        const vesselData = getSimplifiedData(vessel);
        return lastValueFrom(
            this.http.post(
                `${this.domain}/api/vessel/duplicate`,
                { vesselData, previousVesselId, skipGuard: true },
                {
                    headers: this.headers,
                }
            )
        );
    }

    async deleteVessel(vesselId: string, shipyardId: string, iAPIVesselId: string, shipyardiApi: string) {
        return lastValueFrom(
            this.http.delete(`${this.domain}/api/vessel/${shipyardId}/${vesselId}`, {
                body: { skipGuard: true, shipyardiApi, iAPIVesselId },
            })
        );
    }

    async update(
        vesselData: {
            name: string;
            number: string;
            numberOps: number;
            numberFcu: number;
            drawingId: string;
        },
        vessel: Vessel
    ) {
        const { name, number, numberOps, numberFcu, drawingId } = vesselData;
        const newVesselData = {
            name,
            number,
            numberOps,
            numberFcu,
            drawingId,
            iAPIVesselId: vessel.iAPIVesselId,
            shipyardId: vessel.shipyardId,
            skipGuard: true,
        };
        return lastValueFrom(
            this.http.put(`${this.domain}/api/vessel/${vessel.id}`, newVesselData, {
                headers: this.headers,
            })
        );
    }

    async updateProjectStatus(vesselId: string, projectStatus: ProjectStatus) {
        return lastValueFrom(this.http.put(`${this.domain}/api/vessel/projectStatus/${vesselId}`, { projectStatus }));
    }

    async updateDrawingId(vesselId: string, drawingId: string) {
        return lastValueFrom(this.http.put(`${this.domain}/api/vessel/drawingId/${vesselId}`, { drawingId }));
    }

    async startVersion(vessel: Vessel, shipyardiAPi: string, referenceVersion: string, isFirstVersion = false) {
        const vesselData = getSimplifiedData(vessel);
        const versionData = {
            vessel: vesselData,
            shipyardiAPi,
            referenceVersion,
            isFirstVersion,
            skipGuard: true,
        };
        return lastValueFrom(
            this.http.put(`${this.domain}/api/vessel/startVersion/${vessel.id}`, versionData, {
                headers: this.headers,
            })
        );
    }

    async unlockVessel(vesselId, shipyardiApi, vesseliApi, version) {
        const blobParameters = {
            shipyardiApi,
            vesseliApi,
            version,
            skipGuard: true,
        };
        return lastValueFrom(
            this.http.put(`${this.domain}/api/vessel/unlock/${vesselId}`, blobParameters, {
                headers: this.headers,
            })
        );
    }

    async deleteVersion(vesselId, shipyardiApi, vesseliApi, projectVersion): Promise<void> {
        const blobParameters = {
            shipyardiApi,
            vesseliApi,
            projectVersion,
        };
        return lastValueFrom(
            this.http.post<void>(`${this.domain}/api/vessel/deleteVersion/${vesselId}`, blobParameters, {
                headers: this.headers,
            })
        );
    }

    async hasPreviousVersion(vesselId, vesseliApi, shipyardiApi): Promise<{ hasPreviousVersion: boolean }> {
        const currentEnv = this.environmentService.getEnvironment();
        if (currentEnv == RuntimeEnvironments.ELECTRON) return { hasPreviousVersion: false };
        const blobParameters = {
            shipyardiApi,
            vesseliApi,
        };
        return lastValueFrom(
            this.http.get<{ hasPreviousVersion: boolean }>(`${this.domain}/api/vessel/hasPreviousVersion/${vesselId}`, {
                headers: this.headers,
                params: blobParameters,
            })
        );
    }

    async getVesselByVersion(vesselId: string, version: string): Promise<Vessel> {
        return lastValueFrom(this.http.get<Vessel>(`${this.domain}/api/vessel/version/${vesselId}/${version}`));
    }

    private updateCanEditVessel() {
        const currentUser = this.authService.getCurrentUser();
        const currentVessel = this.activeVessel;
        if (!currentVessel || !currentUser) {
            this._canEditVessel = false;
            this._accessUpdated$.next(this._canEditVessel);
            return;
        }

        this._canEditVessel = true;
        if (currentVessel.projectStatus !== ProjectStatus.Editing) {
            this._canEditVessel = false;
        } else {
            if (currentUser.oid != currentVessel.ownerOid) {
                const checkGuestPermission = currentVessel.VesselAccessRequests?.some(
                    request =>
                        request.userOid === currentUser.oid && request.requestStatus === AccessRequestStatus.Editing
                );

                if (!checkGuestPermission) {
                    this._canEditVessel = false;
                }
            } else if (currentUser.oid === currentVessel.ownerOid) {
                this._canEditVessel = this.isVesselOwnerCanEdit();
            }
        }
        this._accessUpdated$.next(this._canEditVessel);
    }

    private checkEditAccess(accessRequest: AccessRequest) {
        const currentVessel = this.activeVessel;
        const currentUser = this.authService.getCurrentUser();
        if (accessRequest.vesselId !== currentVessel.id || accessRequest.userOid !== currentUser.oid) return;
        this._canEditVessel = accessRequest.requestStatus === AccessRequestStatus.Editing;
        this._accessUpdated$.next(this._canEditVessel);
    }

    isVesselOwnerCanEdit() {
        const currentUser = this.authService.getCurrentUser();
        const currentVessel = this.activeVessel;
        if (!currentVessel || !currentUser) {
            return false;
        }

        if (currentVessel.projectStatus !== ProjectStatus.Editing) {
            return false;
        } else if (currentUser.oid == currentVessel.ownerOid) {
            return true;
        }
    }

    async finishVersion(vesselId, version) {
        if (this.env !== RuntimeEnvironments.ELECTRON) return;
        return lastValueFrom(
            this.http.put(`${this.domain}/api/vessel/finishVersion`, {
                version,
                vesselId,
                skipGuard: true,
            })
        );
    }
}
